import { t } from '@app/i18n';
import type { ApiBody, ApiData, ApiQuery } from '@app/api/typed';
import type { components } from '@app/api/types';

export type User = components['schemas']['User'];
export type Role = components['schemas']['PapelUsuario'];
export type UserCreate = ApiBody<'/api/v1/users', 'post'>;
export type UserUpdate = ApiBody<'/api/v1/users/{id}', 'put'>;
export type UsersQuery = NonNullable<ApiQuery<'/api/v1/users', 'get'>>;
export type UsersSort = NonNullable<UsersQuery['sort']>;
/**
 * Página de usuários com o item tipado.
 *
 * O contrato descreve a resposta como `Paginacao & { items: User[] }`, e a
 * interseção com o `itens: unknown[]` genérico de `Paginacao` derruba o tipo do
 * item — daí o `Omit` aqui e o `as User[]` no hook, num lugar só.
 */
export type UsersPage = Omit<ApiData<'/api/v1/users'>, 'items'> & { items: User[] };
export type CreateUserResult = ApiData<'/api/v1/users', 'post'>;

/** Mínimo do FR-106 para **definir** senha (nunca para entrar — FR-003). */
export const PASSWORD_MIN = 8;

export const ROLES: readonly Role[] = ['admin', 'approver', 'viewer', 'pipeline'];

/** A conta da esteira de CI (aim-57a.4): só anuncia releases pela API e não usa o console. */
export const PIPELINE_ROLE: Role = 'pipeline';

/**
 * O que cada papel pode, em uma linha — vira a segunda linha do combobox.
 *
 * Função, não tabela: um `Record` de texto é avaliado no import e congela o
 * idioma da primeira pintura.
 */
export function roleSummary(role: Role): string {
  return t(`users.role.${role}.summary`);
}

/**
 * Como o papel aparece no filtro e na opção do formulário.
 *
 * Os três papéis de pessoa aparecem pelo **valor do contrato** (`admin`, `approver`,
 * `viewer`), que é o que o servidor grava e por isso não se traduz. A conta da esteira
 * tem nome de gente («Esteira (CI)» / «Pipeline (CI)», protótipo aprovado em 04/10):
 * é por ele que o guia de implantação manda escolhê-la. O selo da lista continua o
 * valor do contrato (`pipeline`).
 */
export function roleLabel(role: Role): string {
  return role === PIPELINE_ROLE ? t('users.role.pipeline.label') : role;
}

/** `true` quando o rótulo é o valor do contrato — e então sai em fonte mono. */
export function roleLabelIsValue(role: Role): boolean {
  return role !== PIPELINE_ROLE;
}

/**
 * O Bypass Approval vale para o papel? Não para a esteira: o Bypass é sobre o candidato
 * de Access Control, que ela não pede, e o servidor o recusa com `bypassNotForPipeline`.
 */
export function bypassApplies(role: Role): boolean {
  return role !== PIPELINE_ROLE;
}

/** Filtro de estado da grade (FR-106) — o contrato tem dois parâmetros para três casos. */
export type StateFilter = 'ativos' | 'desativados' | 'todos';

const STATE_KEY: Record<StateFilter, string> = {
  ativos: 'users.filters.state.active',
  desativados: 'users.filters.state.inactive',
  todos: 'users.filters.state.all',
};

/** Rótulo da pílula de estado. Função pelo mesmo motivo de `roleSummary`. */
export function stateLabel(state: StateFilter): string {
  return t(STATE_KEY[state]);
}

export interface UsersFilterValues {
  q: string;
  role: Role | null;
  state: StateFilter;
  page: number;
  sort: UsersSort;
}

export const FILTERS_INICIAIS: UsersFilterValues = {
  q: '',
  role: null,
  state: 'ativos',
  page: 1,
  sort: 'name',
};

export function hasFilterApplied(filters: UsersFilterValues): boolean {
  return Boolean(filters.q) || filters.role !== null || filters.state !== 'ativos';
}

/**
 * Filtros da tela → query do contrato.
 *
 * `deactivated: true` = só os desativados; `include_deactivated: true` = os dois;
 * nenhum dos dois = só ativos. Mandar o parâmetro com `false` seria pedir outra
 * coisa, então cada caso manda só o que precisa.
 */
export function forQuery(filters: UsersFilterValues): UsersQuery {
  return {
    ...(filters.q.trim() ? { q: filters.q.trim() } : {}),
    ...(filters.role ? { role: [filters.role] } : {}),
    ...(filters.state === 'desativados' ? { deactivated: true } : {}),
    ...(filters.state === 'todos' ? { include_deactivated: true } : {}),
    page: filters.page,
    sort: filters.sort,
  };
}

/** Como o usuário define a senha inicial (FR-106): digitada ou gerada. */
export type ModePassword = 'gerar' | 'digitar';

export interface UserFormValues {
  name: string;
  email: string;
  role: Role;
  modoSenha: ModePassword;
  password: string;
  /** Bypass Approval: o candidato deste usuário é aplicado sem fila (`auto_approve_requests`). */
  autoApproveRequests: boolean;
}

export function userDefaults(user?: User): UserFormValues {
  return {
    name: user?.name ?? '',
    email: user?.email ?? '',
    role: user?.role ?? 'viewer',
    modoSenha: 'gerar',
    password: '',
    autoApproveRequests: user?.auto_approve_requests ?? false,
  };
}

export function forCreate(valores: UserFormValues): UserCreate {
  const generate = valores.modoSenha === 'gerar';
  return {
    name: valores.name.trim(),
    email: valores.email.trim().toLowerCase(),
    role: valores.role,
    generate_password: generate,
    // Escondido para a esteira, o Bypass vai desligado: um `true` esquecido de antes da
    // troca de papel seria o 422 `bypassNotForPipeline` por um campo que a tela nem mostra.
    auto_approve_requests: bypassApplies(valores.role) && valores.autoApproveRequests,
    ...(generate ? {} : { password: valores.password }),
  };
}

/**
 * Edição muda nome, papel e o Bypass Approval — o e-mail é imutável (12.2).
 *
 * O campo vai SEMPRE, inclusive desligado: é o mesmo passo da coluna nova no PUT. No
 * servidor ausente significa «mantém», e por isso desligar exige mandar `false`.
 */
export function forUpdate(valores: UserFormValues): UserUpdate {
  return {
    name: valores.name.trim(),
    role: valores.role,
    // Mesma regra do cadastro: passar a esteira desliga o Bypass, e o servidor confere.
    auto_approve_requests: bypassApplies(valores.role) && valores.autoApproveRequests,
  };
}
